import type { BoardDoc } from '../doc/BoardDoc';
import { newId } from '../doc/BoardDoc';
import { rotatedBounds, unionRects, type Point, type Rect } from '../doc/geometry';
import {
  DEFAULT_KANBAN_SIZE,
  DEFAULT_LINK_SIZE,
  DEFAULT_TABLE_SIZE,
  DEFAULT_TODO_SIZE,
  DEFAULT_NOTE_SIZE,
  DEFAULT_RICHTEXT_SIZE,
  DEFAULT_SHAPE_SIZE,
  DEFAULT_TITLE_SIZE,
  NOTE_COLORS,
  type Geometry,
  type Item,
  type ItemPatch,
  type NewItem,
} from '../doc/schema';
import { api } from '../sync/api';
import type { PaneStore } from './paneStore';
import { toolSettings } from './toolSettings';
import { loadRich } from './text/richLoader';
import { cloneAssetLocally } from './assets/localAssets';
import { enqueueUpload } from '../sync/uploadQueue';
import { RICH_TYPES } from '../doc/schema';
import { centerOn, fitRect, screenToWorld, visibleWorldRect, zoomAt, type Camera } from './viewport/camera';

export interface Ctx {
  board: BoardDoc;
  pane: PaneStore;
}

// ---- selection helpers ------------------------------------------------------

export function selectedItems({ board, pane }: Ctx): Item[] {
  const out: Item[] = [];
  for (const id of pane.getState().selection) {
    const item = board.index.get(id);
    if (item) out.push(item);
  }
  return out;
}

export function selectionBounds(ctx: Ctx): Rect | null {
  const { transient } = ctx.pane.getState();
  return unionRects(selectedItems(ctx).map((i) => rotatedBounds(withTransient(i, transient[i.id]))));
}

export function withTransient<T extends Geometry>(item: T, t: Partial<Geometry> | undefined): T {
  return t ? { ...item, ...t } : item;
}

export function selectAll({ board, pane }: Ctx): void {
  pane.getState().select(board.index.all().filter((i) => !board.isHidden(i) && !board.isLocked(i)).map((i) => i.id));
}

export function deleteSelection({ board, pane }: Ctx): void {
  const { selection, clearSelection } = pane.getState();
  if (!selection.size) return;
  const ids = [...selection].filter((id) => {
    const item = board.index.get(id);
    return item && !board.isLocked(item);
  });
  if (ids.length) board.deleteItems(ids);
  clearSelection();
}

/** Writes the in-progress transient geometry to the document in one undoable transaction. */
export function commitTransient({ board, pane }: Ctx): void {
  const { transient, clearTransient } = pane.getState();
  const patches: [string, ItemPatch][] = [];
  for (const [id, g] of Object.entries(transient)) {
    const patch: ItemPatch = {};
    if (g.x !== undefined) patch.x = round(g.x);
    if (g.y !== undefined) patch.y = round(g.y);
    if (g.w !== undefined) patch.w = round(g.w);
    if (g.h !== undefined) patch.h = round(g.h);
    if (g.rotation !== undefined) patch.rotation = g.rotation;
    patches.push([id, patch]);
  }
  if (patches.length) board.updateItems(patches);
  clearTransient();
}

const round = (v: number) => Math.round(v * 100) / 100;

export function nudgeSelection({ board, pane }: Ctx, dx: number, dy: number): void {
  const patches: [string, ItemPatch][] = [];
  for (const id of pane.getState().selection) {
    const item = board.index.get(id);
    if (item) patches.push([id, { x: item.x + dx, y: item.y + dy }]);
  }
  if (patches.length) board.updateItems(patches);
}

// ---- creating items ---------------------------------------------------------

export function createNoteAt(ctx: Ctx, world: Point, edit = true): string {
  const size = DEFAULT_NOTE_SIZE;
  const id = ctx.board.createItem({
    type: 'note',
    x: world.x - size / 2,
    y: world.y - size / 2,
    w: size,
    h: size,
    props: { text: '', color: NOTE_COLORS[0], fontSize: 20 },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export function createTitleAt(ctx: Ctx, world: Point, edit = true): string {
  const { w, h } = DEFAULT_TITLE_SIZE;
  const id = ctx.board.createItem({
    type: 'title',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { text: '', fontSize: 40, bold: true, align: 'left' },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export function createRichTextAt(ctx: Ctx, world: Point, edit = true): string {
  const { w, h } = DEFAULT_RICHTEXT_SIZE;
  const id = ctx.board.createItem({
    type: 'richtext',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { bg: null, fontSize: 16 },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export async function createTableAt(ctx: Ctx, world: Point, edit = true): Promise<string> {
  await loadRich();
  const { w, h } = DEFAULT_TABLE_SIZE;
  const id = ctx.board.createItem({
    type: 'table',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { bg: null, fontSize: 14, contentJson: (await loadRich()).tableJson(3, 3) },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export async function createTodoAt(ctx: Ctx, world: Point, edit = true): Promise<string> {
  await loadRich();
  const { w, h } = DEFAULT_TODO_SIZE;
  const id = ctx.board.createItem({
    type: 'todo',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { bg: null, fontSize: 15, contentJson: (await loadRich()).taskListJson(['']) },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

export function createKanbanAt(ctx: Ctx, world: Point, edit = true): string {
  const { w, h } = DEFAULT_KANBAN_SIZE;
  const id = ctx.board.createItem({
    type: 'kanban',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: {
      columns: [
        { id: newId(), title: 'To do', cards: [] },
        { id: newId(), title: 'Doing', cards: [] },
        { id: newId(), title: 'Done', cards: [] },
      ],
    },
  });
  const s = ctx.pane.getState();
  s.select([id]);
  if (edit) s.setEditing(id);
  return id;
}

/** Creates a shape from the current tool settings, either in a dragged rect or centred on a point. */
export function createShapeAt(ctx: Ctx, where: Rect | Point, opts: { flip?: boolean } = {}): string {
  const t = toolSettings.getState();
  const rect: Rect =
    'w' in where
      ? where
      : {
          x: where.x - DEFAULT_SHAPE_SIZE / 2,
          y: where.y - DEFAULT_SHAPE_SIZE / 2,
          w: DEFAULT_SHAPE_SIZE,
          h: DEFAULT_SHAPE_SIZE,
        };
  const isLine = t.shapeKind === 'line' || t.shapeKind === 'arrow';
  const id = ctx.board.createItem({
    type: 'shape',
    x: round(rect.x),
    y: round(rect.y),
    w: round(Math.max(1, rect.w)),
    h: round(Math.max(isLine ? 0 : 1, rect.h)),
    props: {
      kind: t.shapeKind,
      fill: isLine ? 'none' : t.fill,
      stroke: t.stroke === 'none' && (isLine || t.fill === 'none') ? '#111111' : t.stroke,
      strokeWidth: t.strokeWidth,
      flip: opts.flip ?? false,
    },
  });
  ctx.pane.getState().select([id]);
  return id;
}

const URL_RE = /^https?:\/\/[^\s]+$/i;
export const looksLikeUrl = (text: string): boolean => URL_RE.test(text.trim());

/** Creates a link card and fills it in from the server's preview endpoint. */
export function createLinkAt(ctx: Ctx, url: string, world: Point): string {
  const { w, h } = DEFAULT_LINK_SIZE;
  url = url.trim();
  const id = ctx.board.createItem({
    type: 'link',
    x: world.x - w / 2,
    y: world.y - h / 2,
    w,
    h,
    props: { url, title: '', description: '', image: null, site: '', status: 'loading' },
  });
  ctx.pane.getState().select([id]);
  void refreshLinkPreview(ctx.board, id);
  return id;
}

interface Preview {
  url: string;
  title: string;
  description: string;
  image: string | null;
  site: string;
}

export async function refreshLinkPreview(board: BoardDoc, id: string): Promise<void> {
  const item = board.index.get(id);
  if (!item || item.type !== 'link') return;
  try {
    const p = await api.post<Preview>('/link-preview', { url: item.props.url, boardId: board.remote ? board.boardId : undefined });
    if (!board.index.has(id)) return;
    board.updateItem(id, {
      props: { title: p.title, description: p.description, image: p.image, site: p.site, status: 'ok' },
    });
  } catch {
    if (board.index.has(id)) board.updateItem(id, { props: { status: 'error' } });
  }
}

/** World point at the centre of the pane. */
export function viewCenter(pane: PaneStore): Point {
  const { camera, size } = pane.getState();
  return screenToWorld(camera, { x: size.w / 2, y: size.h / 2 });
}

// ---- clipboard --------------------------------------------------------------

// Module-level so two panes in one tab share it. System clipboard integration comes later.
let clipboard: Item[] = [];
let clipboardBoard = '';
let pasteCount = 0;

const hasRich = (items: Item[]) => items.some((i) => RICH_TYPES.has(i.type));

/** Rewrites image/file props to freshly cloned assets when items cross into another board. */
async function relinkAssets(items: NewItem[], fromBoard: string, to: BoardDoc): Promise<void> {
  if (fromBoard === to.boardId) return;
  for (const item of items) {
    if (item.type !== 'image' && item.type !== 'file') continue;
    const newId = await cloneAssetLocally(item.props.assetId);
    if (!newId) continue;
    item.props = { ...item.props, assetId: newId } as typeof item.props;
    if (to.remote) void enqueueUpload(to.boardId, newId);
  }
}

export async function copySelection(ctx: Ctx): Promise<void> {
  const items = selectedItems(ctx);
  if (!items.length) return;
  if (hasRich(items)) await loadRich();
  clipboard = ctx.board.snapshotItems(items.map((i) => i.id));
  clipboardBoard = ctx.board.boardId;
  pasteCount = 0;
}

export async function cutSelection(ctx: Ctx): Promise<void> {
  await copySelection(ctx);
  deleteSelection(ctx);
}

export async function pasteClipboard(ctx: Ctx, at?: Point): Promise<void> {
  if (!clipboard.length) return;
  if (hasRich(clipboard)) await loadRich();
  pasteCount++;
  const offset = at ? null : 24 * pasteCount;
  const bounds = unionRects(clipboard.map(rotatedBounds));
  const inputs: NewItem[] = clipboard.map((item) => {
    const { id: _id, z: _z, createdAt: _c, updatedAt: _u, ...rest } = item;
    void _id;
    void _z;
    void _c;
    void _u;
    let x = item.x;
    let y = item.y;
    if (offset !== null) {
      x += offset;
      y += offset;
    } else if (at && bounds) {
      x = at.x - bounds.w / 2 + (item.x - bounds.x);
      y = at.y - bounds.h / 2 + (item.y - bounds.y);
    }
    return { ...rest, x, y, id: newId() } as NewItem;
  });
  await relinkAssets(inputs, clipboardBoard, ctx.board);
  const ids = ctx.board.createItems(inputs);
  ctx.pane.getState().select(ids);
}

export async function duplicateSelection(ctx: Ctx): Promise<void> {
  const items = selectedItems(ctx);
  if (!items.length) return;
  if (hasRich(items)) await loadRich();
  const snapshot = ctx.board.snapshotItems(items.map((i) => i.id));
  const inputs: NewItem[] = snapshot.map((item) => {
    const { id: _id, z: _z, createdAt: _c, updatedAt: _u, ...rest } = item;
    void _id;
    void _z;
    void _c;
    void _u;
    return { ...rest, x: item.x + 24, y: item.y + 24 } as NewItem;
  });
  ctx.pane.getState().select(ctx.board.createItems(inputs));
}

export function hasClipboard(): boolean {
  return clipboard.length > 0;
}

// ---- camera -----------------------------------------------------------------

export function zoomBy(pane: PaneStore, factor: number, screenPt?: Point): void {
  const { camera, size, setCamera } = pane.getState();
  const pt = screenPt ?? { x: size.w / 2, y: size.h / 2 };
  setCamera(zoomAt(camera, pt, camera.zoom * factor));
}

export function zoomTo(pane: PaneStore, zoom: number): void {
  const { camera, size, setCamera } = pane.getState();
  setCamera(zoomAt(camera, { x: size.w / 2, y: size.h / 2 }, zoom));
}

export function zoomToFit(ctx: Ctx): void {
  const all = ctx.board.index.all();
  const bounds = unionRects(all.map(rotatedBounds));
  const { size, setCamera } = ctx.pane.getState();
  if (!bounds) {
    setCamera(centerOn({ x: 0, y: 0 }, size, 1));
    return;
  }
  setCamera(fitRect(bounds, size, 64, 4));
}

export function zoomToSelection(ctx: Ctx): void {
  const bounds = selectionBounds(ctx);
  if (!bounds) return zoomToFit(ctx);
  const { size, setCamera } = ctx.pane.getState();
  setCamera(fitRect(bounds, size, 96, 4));
}

export function visibleRect(pane: PaneStore): Rect {
  const { camera, size } = pane.getState();
  return visibleWorldRect(camera, size);
}

export function cameraFor(pane: PaneStore): Camera {
  return pane.getState().camera;
}

// ---- cross-pane / cross-board ----------------------------------------------------

/** Copies items into another board at new positions (split view drag between different boards). */
export async function copyItemsToBoard(
  from: BoardDoc,
  to: BoardDoc,
  positions: Map<string, Point>,
): Promise<string[]> {
  const ids = [...positions.keys()];
  const source = ids.map((id) => from.index.get(id)).filter((i): i is Item => !!i);
  if (hasRich(source)) await loadRich();
  const snapshot = from.snapshotItems(ids);
  const inputs: NewItem[] = snapshot.map((item) => {
    const { id: _id, z: _z, createdAt: _c, updatedAt: _u, groupId: _g, layerId: _l, ...rest } = item;
    void _id;
    void _z;
    void _c;
    void _u;
    void _g;
    void _l;
    const p = positions.get(item.id) ?? { x: item.x, y: item.y };
    return { ...rest, x: p.x, y: p.y } as NewItem;
  });
  await relinkAssets(inputs, from.boardId, to);
  return to.createItems(inputs);
}

// ---- dev helpers ------------------------------------------------------------

export function stressTest(ctx: Ctx, count: number): void {
  const center = viewCenter(ctx.pane);
  const spread = Math.sqrt(count) * 260;
  const inputs: NewItem[] = [];
  for (let i = 0; i < count; i++) {
    inputs.push({
      type: 'note',
      x: center.x + (Math.random() - 0.5) * spread,
      y: center.y + (Math.random() - 0.5) * spread,
      w: 160 + Math.random() * 120,
      h: 160 + Math.random() * 120,
      rotation: Math.round((Math.random() - 0.5) * 20),
      props: {
        text: `Note ${i + 1}`,
        color: NOTE_COLORS[i % NOTE_COLORS.length]!,
        fontSize: 20,
      },
    });
  }
  ctx.board.createItems(inputs);
}

// ---- export -----------------------------------------------------------------

/** Downloads the board's items as JSON (assets referenced by id; the files come with a later export). */
export function exportBoardJson(board: BoardDoc): void {
  const items = board.index.all();
  const assetIds = [...new Set(items.filter((i) => i.type === 'image').map((i) => (i as { props: { assetId: string } }).props.assetId))];
  const payload = { format: 'vds-board', version: 1, boardId: board.boardId, exportedAt: new Date().toISOString(), items, assetIds };
  const blob = new Blob([JSON.stringify(payload, null, 2)], { type: 'application/json' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `vds-${board.boardId}.json`;
  a.click();
  setTimeout(() => URL.revokeObjectURL(url), 10000);
}
